/**
 * Lagrede månedsrapporter (#292): every file the active company's export log holds, across months,
 * newest first, each taken again as it was stored (GET /admin/pay-files, /admin/pay-files/:id,
 * services/payMonth.js savedFiles). Not a recalculation: today's hours and rates do not reach an old
 * file. Listing and taking a file need pay.export, as the export itself; a reader without it is told so.
 */
import { get, saveFile } from '@/api/client';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { useTenant } from '@/shell/tenant';
import { Button, Skeleton, toast } from '@/ui';
import { Icon } from '@/ui/icons';
import { Notice } from '@/ui/page';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { monthLabel } from '../Omsetning';
import { when } from './Month';

export interface SavedFile {
  id: number;
  format: 'csv' | 'tripletex_csv' | 'poweroffice_csv' | 'visma_csv' | 'xlsx' | 'pdf';
  period_start: string;
  period_end: string;
  exported_at: string;
  lines: number;
  file_name: string;
  exported_by_name: string;
  scope: { stores: number; people: number | null } | null;
}

/** the same names as «Last ned fil» gives the files (Lonn.tsx FILES) */
export const FILE_LABEL: Record<SavedFile['format'], string> = {
  csv: 'CSV-rapport',
  xlsx: 'Excel-rapport',
  pdf: 'PDF-rapport',
  tripletex_csv: 'Tripletex-fil',
  poweroffice_csv: 'PowerOffice-fil',
  visma_csv: 'Visma-fil',
};

export function SavedReports() {
  const can = useCan();
  const { tenant } = useTenant();
  const may = can('timerloenn.sende');
  const q = useQuery({
    // the 'report' prefix: a file fetched on the page refreshes this list too (invalidatePayMonth's sibling)
    queryKey: ['report', 'pay-files', tenant?.id],
    queryFn: () => get<{ files: SavedFile[] }>('/admin/pay-files'),
    enabled: !!tenant && may,
  });
  // ?fil= is one saved file, as Hugin's report card opens it (#287): its month is the filter and its row is marked
  const [sp] = useSearchParams();
  const asked = /^\d{1,9}$/.test(sp.get('fil') ?? '') ? Number(sp.get('fil')) : null;
  const [picked, setMonth] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  if (!may)
    return (
      <Notice tone="info" title={T('Du har ikke tilgang til lønnseksporten')}>
        {T('Lagrede rapporter er filene som har gått ut. Spør administratoren om tilgang.')}
      </Notice>
    );
  if (q.isPending) return <Skeleton rows={3} />;
  if (q.isError)
    return (
      <Notice
        tone="dgr"
        title={T('Kunne ikke hente lagrede rapporter')}
        action={
          <Button small onClick={() => q.refetch()}>
            {T('Prøv igjen')}
          </Button>
        }
      >
        {(q.error as Error).message}
      </Notice>
    );
  const files = q.data.files;
  const target = asked == null ? null : (files.find((x) => x.id === asked) ?? null);
  const month = picked ?? (target ? target.period_start.slice(0, 7) : '');
  const months = [...new Set(files.map((x) => x.period_start.slice(0, 7)))];
  const shown = month ? files.filter((x) => x.period_start.startsWith(month)) : files;
  const take = async (x: SavedFile) => {
    setBusy(x.id);
    try {
      await saveFile(`/admin/pay-files/${x.id}`, x.file_name);
    } catch (e) {
      toast(`${T('Kunne ikke hente filen')}: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="lhist" data-testid="saved-reports">
      <p>
        {T(
          'Hver fil som er lastet ned, lagres slik den var. Nye filer erstatter ikke de gamle, og senere endringer i timene endrer dem ikke.',
        )}
      </p>
      {asked != null && !target && (
        <Notice tone="warn" title={T('Fant ikke rapporten')}>
          {T(
            'Den finnes ikke i denne bedriftens lagrede rapporter, eller du har ikke tilgang til den. Listen viser det du har.',
          )}
        </Notice>
      )}
      {files.length === 0 ? (
        <p>{T('Ingen lagrede rapporter ennå. En fil lagres her når den lastes ned under Last ned fil.')}</p>
      ) : (
        <>
          <label className="mf">
            {T('Måned')}
            <select className="inp" value={month} onChange={(e) => setMonth(e.target.value)}>
              <option value="">{T('Alle måneder')}</option>
              {months.map((m) => (
                <option key={m} value={m}>
                  {monthLabel(m)}
                </option>
              ))}
            </select>
          </label>
          <ul>
            {shown.map((x) => (
              <li
                key={x.id}
                className={x.id === asked ? 'lsaved lsaved-on' : 'lsaved'}
                aria-current={x.id === asked ? 'true' : undefined}
              >
                <span>
                  <b>
                    {monthLabel(x.period_start.slice(0, 7))}, {T(FILE_LABEL[x.format])}
                  </b>
                  <small>
                    {T('Lagret {when}, {who}', { when: when(x.exported_at), who: x.exported_by_name })}
                  </small>
                  <small>
                    {x.file_name}, {x.lines} {x.lines === 1 ? T('linje') : T('linjer')}
                    {x.scope?.people != null && `, ${T('{n} valgte personer', { n: x.scope.people })}`}
                  </small>
                </span>
                <Button
                  small
                  onClick={() => take(x)}
                  disabled={busy === x.id}
                  aria-label={`${T('Last ned')} ${x.file_name}`}
                >
                  <Icon name="download" />
                  {T('Last ned')}
                </Button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
